import { useState, useRef, useLayoutEffect, type ReactNode, useMemo } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import type { MenuLink, NavItem, MenuItemSection } from '@/interfaces/strapi';
import CTNIcon from '@ctn/ui/components/ctn-icon/icon';
import { SECTIONS } from '@/constants/routes.constants';
import { norm, trimSlash } from '@/utils';
import { usePrefetch } from '@/hooks';

type MenuTreeProps = {
	readonly items: NavItem[];
	readonly variant: 'desktop' | 'mobile';
	readonly classNameBase: string;
	readonly onNavigate?: () => void;
	readonly activeOverride?: string | string[];
};

function Collapsible({
	isOpen,
	className,
	id,
	children,
}: {
	readonly isOpen: boolean;
	readonly className?: string;
	readonly id?: string;
	readonly children: ReactNode;
}) {
	const listenerRef = useRef<((e: TransitionEvent) => void) | null>(null);
	const ref = useRef<HTMLDivElement>(null);
	const first = useRef(true);

	const msToNumber = (v: string) => {
		const s = v.trim();
		if (s.endsWith('ms')) return parseFloat(s);
		if (s.endsWith('s')) return parseFloat(s) * 1000;
		return parseFloat(s) || 0;
	};
	const numToMs = (n: number) => `${Math.max(0, Math.round(n))}ms`;

	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return;

		const styles = getComputedStyle(document.documentElement);
		const openMs = styles.getPropertyValue('--acc-open-ms')?.trim() || '320ms';
		const closeMs = styles.getPropertyValue('--acc-close-ms')?.trim() || '280ms';
		const ease = styles.getPropertyValue('--acc-ease')?.trim() || 'cubic-bezier(0.2,0,0,1)';

		const openDur = msToNumber(openMs);
		const closeDur = msToNumber(closeMs);
		const openTrans = `height ${numToMs(openDur)} ${ease}, opacity ${numToMs(openDur * 0.7)} ${ease}`;
		const closeTrans = `height ${numToMs(closeDur)} ${ease}, opacity ${numToMs(closeDur * 0.7)} ${ease}`;

		const reduceMotion =
			typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

		const cleanup = () => {
			if (listenerRef.current && el) {
				el.removeEventListener('transitionend', listenerRef.current);
				listenerRef.current = null;
			}
		};

		// Estado inicial (sin animación)
		if (first.current) {
			first.current = false;
			cleanup();
			el.style.transition = 'none';
			el.style.height = isOpen ? 'auto' : '0px';
			el.style.opacity = isOpen ? '1' : '0';
			return;
		}

		if (reduceMotion) {
			cleanup();
			el.style.transition = 'none';
			el.style.height = isOpen ? 'auto' : '0px';
			el.style.opacity = isOpen ? '1' : '0';
			return;
		}

		el.style.transition = 'none';
		cleanup();

		if (isOpen) {
			// Abrir: 0 -> scrollHeight -> auto
			el.style.height = '0px';
			el.style.opacity = '0';

			requestAnimationFrame(() => {
				const full = el.scrollHeight;
				void el.offsetHeight;

				el.style.transition = openTrans;
				el.style.height = `${full}px`;
				el.style.opacity = '1';

				const onEnd = (e: TransitionEvent) => {
					if (e.propertyName === 'height') {
						el.style.transition = 'none';
						el.style.height = 'auto';
						cleanup();
					}
				};
				listenerRef.current = onEnd;
				el.addEventListener('transitionend', onEnd);
			});
		} else {
			// Cerrar: auto/px -> 0
			const current =
				el.style.height && el.style.height !== 'auto' ? parseFloat(el.style.height) : el.scrollHeight;

			el.style.height = `${current}px`;
			el.style.opacity = '1';

			requestAnimationFrame(() => {
				void el.offsetHeight;

				el.style.transition = closeTrans;
				el.style.height = '0px';
				el.style.opacity = '0';

				const onEnd = (e: TransitionEvent) => {
					if (e.propertyName === 'height') {
						cleanup();
					}
				};
				listenerRef.current = onEnd;
				el.addEventListener('transitionend', onEnd);
			});
		}

		return cleanup;
	}, [isOpen]);

	return (
		<div ref={ref} id={id} className={className} aria-hidden={!isOpen}>
			{children}
		</div>
	);
}

export const MenuTree = ({ items, variant, classNameBase, onNavigate, activeOverride }: MenuTreeProps) => {
	if (variant === 'desktop') {
		return <DesktopMenu items={items} base={classNameBase} activeOverride={activeOverride} />;
	}
	return <MobileMenu items={items} base={classNameBase} onNavigate={onNavigate} />;
};

/* ================= DESKTOP ================= */
function DesktopMenu({
	items,
	base,
	activeOverride,
}: {
	readonly items: NavItem[];
	readonly base: string;
	readonly activeOverride?: string | string[];
}) {
	const B = base;
	const { pathname } = useLocation();
	const { prefetch, cancel } = usePrefetch();

	const overrideList = useMemo<string[]>(() => {
		if (!activeOverride) return [];
		return Array.isArray(activeOverride) ? activeOverride : [activeOverride];
	}, [activeOverride]);

	const overrideSet = useMemo(() => new Set(overrideList.map((x) => trimSlash(norm(x)))), [overrideList]);

	// Raíces de sección conocidas (para end={false} en items raíz)
	const sectionRoots = useMemo(() => new Set(Object.values(SECTIONS).map((x) => trimSlash(x))), []);

	// Helpers para enlaces
	const isExternalLink = (lnk: MenuLink) => lnk.isExternal || lnk.openInNewTab;
	const hrefFor = (lnk: MenuLink) => (lnk.isExternal ? (lnk.externalUrl ?? lnk.url) : norm(lnk.url));

	return (
		<nav className={`${B}__menu`} aria-label="Navegación principal">
			<ul>
				{items.map((item, idx) => {
					const hasDropdown = Array.isArray(item.sections) && item.sections.length > 0;

					// ===== Item SIN dropdown (enlace directo) =====
					if (!hasDropdown) {
						const to = trimSlash(norm(item.url));
						const isSectionRoot = sectionRoots.has(to);

						return (
							<li key={item.name ?? idx} className={`${B}__item`}>
								<NavLink
									to={to}
									end={!isSectionRoot}
									className={({ isActive }) =>
										`${B}__link${isActive || overrideSet.has(to) ? ' is-active' : ''}`
									}
									onMouseEnter={() => prefetch(to)}
									onMouseLeave={cancel}
									onFocus={() => prefetch(to)}
								>
									{item.name}
								</NavLink>
							</li>
						);
					}

					// ===== Item CON dropdown =====
					const sections: MenuItemSection[] = item.sections ?? [];

					// Rutas internas de hijos (para marcar activo por prefijo)
					const internalChildPaths = sections
						.flatMap((s) => s.links ?? [])
						.filter((l) => !isExternalLink(l))
						.map((l) => trimSlash(norm(l.url)));

					const isParentActive =
						internalChildPaths.some((u) => pathname === u || pathname.startsWith(u + '/')) ||
						(overrideList.length > 0 &&
							internalChildPaths.some((u) =>
								overrideList.some(
									(root) => u === trimSlash(norm(root)) || u.startsWith(trimSlash(norm(root)) + '/'),
								),
							));

					return (
						<li
							key={item.name ?? idx}
							className={`${B}__item ${B}__item--hasDropdown${isParentActive ? ' is-active' : ''}`}
							tabIndex={idx + 1}
						>
							<button
								type="button"
								className={`${B}__link ${B}__link--button${isParentActive ? ' is-active' : ''}`}
								aria-haspopup="menu"
								aria-expanded="false"
							>
								{item.name}
							</button>

							<div className={`${B}__dropdown`} role="menu" aria-label={item.name}>
								{sections.map((section) => (
									<div key={section.id ?? section.heading} className={`${B}__dropdown-section`}>
										<ul className={`${B}__dropdown-list`}>
											{(section.links ?? []).map((lnk) => {
												const isExt = isExternalLink(lnk);
												const href = hrefFor(lnk);
												const key = lnk.id ?? lnk.url ?? lnk.externalUrl ?? href;

												if (isExt) {
													return (
														<li key={key}>
															<a
																href={href}
																target="_blank"
																rel="noopener noreferrer"
																className={`${B}__dropdown-link`}
																aria-label={lnk.description ?? lnk.name}
															>
																{lnk.name}
															</a>
														</li>
													);
												}

												const to = trimSlash(href);
												return (
													<li key={key}>
														<NavLink
															to={to}
															aria-label={lnk.description ?? lnk.name}
															className={({ isActive }) =>
																`${B}__dropdown-link${isActive || overrideSet.has(to) ? ' is-active' : ''}`
															}
															onMouseEnter={() => prefetch(to)}
															onMouseLeave={cancel}
															onFocus={() => prefetch(to)}
														>
															{lnk.name}
														</NavLink>
													</li>
												);
											})}
										</ul>
									</div>
								))}
							</div>
						</li>
					);
				})}
			</ul>
		</nav>
	);
}

/* ================= MOBILE / TABLET (Drawer + accordeon) ================= */
function MobileMenu({
	items,
	base,
	onNavigate,
}: {
	readonly items: NavItem[];
	readonly base: string;
	readonly onNavigate?: () => void;
}) {
	const B = base;
	const [openIdx, setOpenIdx] = useState<number | null>(null);

	const isExternalLink = (lnk: MenuLink) => lnk.isExternal || lnk.openInNewTab;
	const hrefFor = (lnk: MenuLink) => (lnk.isExternal ? (lnk.externalUrl ?? lnk.url) : norm(lnk.url));
	const { prefetch, cancel } = usePrefetch();

	return (
		<div className={`${B}__container`}>
			<nav className={`${B}__mnav`} aria-label="Menú móvil desplegable">
				<ul className={`${B}__mlist`}>
					{items.map((item, idx) => {
						const hasSections = Array.isArray(item.sections) && item.sections.length > 0;

						// Item simple
						if (!hasSections) {
							const to = item.url?.startsWith('/') ? item.url : `/${item.url ?? ''}`;
							return (
								<li key={item.name ?? idx} className={`${B}__mitem`}>
									<Link
										to={to}
										className={`${B}__mlink`}
										onClick={onNavigate}
										onMouseEnter={() => prefetch(to)}
										onFocus={() => prefetch(to)}
									>
										{item.name}
									</Link>
								</li>
							);
						}

						// Item con sub-secciones (acordeón)
						const isOpen = openIdx === idx;
						return (
							<li key={item.name ?? idx} className={`${B}__mitem ${B}__mitem--parent`}>
								<button
									type="button"
									className={`${B}__mparent`}
									aria-expanded={isOpen}
									aria-controls={`${B}__mpanel-${idx}`}
									onClick={() => setOpenIdx(isOpen ? null : idx)}
								>
									<span>{item.name}</span>
									<CTNIcon icon={isOpen ? 'arrowUp' : 'arrowDown'} className={`${B}__mcaret`} />
								</button>

								<Collapsible id={`${B}__mpanel-${idx}`} className={`${B}__mpanel`} isOpen={isOpen}>
									<ul className={`${B}__msublist`}>
										{(item.sections ?? []).flatMap((section) =>
											(section.links ?? []).map((lnk) => {
												const isExt = isExternalLink(lnk);
												const href = hrefFor(lnk);
												const key = lnk.id ?? lnk.url ?? lnk.externalUrl ?? href;

												return (
													<li key={key}>
														{isExt ? (
															<a
																href={href}
																target="_blank"
																rel="noopener noreferrer"
																className={`${B}__msublink`}
																aria-label={lnk.description ?? lnk.name}
																onClick={onNavigate}
															>
																{lnk.name}
															</a>
														) : (
															<Link
																to={href}
																className={`${B}__msublink`}
																aria-label={lnk.description ?? lnk.name}
																onClick={onNavigate}
																onMouseEnter={() => prefetch(href)}
																onFocus={() => prefetch(href)}
															>
																{lnk.name}
															</Link>
														)}
													</li>
												);
											}),
										)}
									</ul>
								</Collapsible>
							</li>
						);
					})}
				</ul>
			</nav>
		</div>
	);
}
